import { Layout } from '@/components/Layout';
import { GoBackButtonFilled } from '@/components/buttons/Buttons';
import { Button } from 'primereact/button';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdDirectionsCar, MdFullscreenExit } from 'react-icons/md';
import RouteDetails from '../../route-details/RouteDetails';
import styles from './expanded-map-modal.module.css';
import { X4bActivityDto, ActivityHelper } from '../../../vehicle/engine/X4bActivityDto';
import { ConciergeLiveMap } from '@/modules/concierge/map/ConciergeLiveMap';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { cn } from '@/components/ui/cn';
import { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import { fetchActivityRouteData } from '@/modules/concierge/map/useInitialRoute';

interface ExpandedMapModalProps {
	isOpen: boolean;
	handleClose: () => void;
	data: X4bActivityDto
}

const ExpandedMapModal: React.FC<ExpandedMapModalProps> = (props) => {
	const [routingData, setRoutingData] = useState<GoogleMapsRoutingResponse | null>(null);

	// Setting trip id and order uuid in the LiveMap component using bind setters
	const tripIdSetterRef = useRef<(tripId: string) => void>(undefined);
	const bindTripIdSetter = useCallback((setter: (tripId: string) => void) => {
		tripIdSetterRef.current = setter;
	}, []);

	const orderUuidSetterRef = useRef<(orderUuid: string) => void>(undefined);
	const bindOrderUuidSetter = useCallback((setter: (orderUuid: string) => void) => {
		orderUuidSetterRef.current = setter;
	}, []);

	// Set the order UUID and fetch trip ID or initial route when the modal opens or activity data changes
	useEffect(() => {
		if (!props.isOpen || !props.data.XpressId) return;

		fetchActivityRouteData(
			props.data,
			props.data.Id,
			props.data.XpressId,
			setRoutingData,
			orderUuidSetterRef,
			tripIdSetterRef
		);
	}, [props.isOpen, props.data.XpressId, props.data.Id, props.data.Status]);

	if (!props.isOpen) return null;

	return (
		// <div className={styles.modalContainer}>
		<div className=" fixed inset-0 bg-surface justify-center items-center z-50 ">
			<Layout includeSidebar={false}>
				<div className="flex flex-col" style={{ height: 'calc(100vh - 132px)' }}>
					<ConciergeLiveMap
						key={props.data.XpressId}
						bindTripIdSetter={bindTripIdSetter}
						bindOrderUuidSetter={bindOrderUuidSetter}
						pickupLocationName={props.data.Pickup?.PlaceName || props.data.Pickup?.Address || undefined}
						dropOffLocationName={props.data.Dropoff?.PlaceName || props.data.Dropoff?.Address || undefined}
						mapDivClassName={cn('w-full flex-1 !min-h-0 object-cover rounded-[28px]')}
						activity={props.data}
						markersAsDots={true}
						initialRoute={routingData}
					//mapDivClassName={cn('w-full !min-h-0 h-full object-cover rounded-[28px]')}
					/>
					<Button
						icon={
							<MdFullscreenExit className="text-white text-[24px]" />
						}
						className={styles.closeButton}
						onClick={props.handleClose}
						aria-label="Exit Fullscreen"
					/>
					<div className={styles.goBackButton}>
						<GoBackButtonFilled
							id="map-back"
							onClick={props.handleClose}
						/>
					</div>
					<div className={styles.infoContainer}>
						<div className={styles.userDetails}>
							<div>
								<p className={styles.userName}>{props.data.Name}</p>
								<p className={styles.userContact}>
									{props.data.HeadPassenger?.Phone}
								</p>
							</div>
							<div>
								<div className={styles.carInfo}>
									<MdDirectionsCar
										className={styles.carIcon}
									/>
									<p className={styles.carPlate}>{props.data.Driver?.Plate}</p>
								</div>
								<div className={styles.carType}>
									<div className={styles.carTypeIcon} />
									<p className={styles.carTypeText}>
										{props.data.Driver?.Vehicle}
									</p>
								</div>
							</div>
						</div>
						<RouteDetails gap="gap-2" activity={props.data} />
					</div>
				</div>
			</Layout>
		</div>
	);
};

export default ExpandedMapModal;
